import React from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle } from 'lucide-react';
import '../styles/Legal.css';
import '../styles/FAQ.css';
import SEO from '../components/SEO';

const FAQ = () => {
  const faqSections = [
    {
      title: "General Questions",
      id: "general",
      questions: [
        {
          q: "What is CADENCE?",
          a: "CADENCE (Clinical Advocacy & Decisive Empowerment, Navigating Care & Expertise) is a patient advocacy and healthcare navigation service. We help patients understand their diagnoses, navigate complex medical systems, and advocate for their health with clarity and&nbsp;confidence."
        },
        {
          q: "Who are your services for?",
          a: "Our services are designed for anyone seeking clarity about their health conditions, those who feel unheard by their current providers, patients with complex or chronic conditions, and anyone who wants a medical professional in their corner to help navigate the healthcare system."
        },
        {
          q: "Do you replace my doctor?",
          a: "No. We work alongside your existing healthcare team. We provide diagnostic clarification, second opinions, and advocacy support, but we do not prescribe medications, perform procedures, or replace your treating physicians. Our role is to empower you with knowledge and support."
        }
      ]
    },
    {
      title: "Services & Pricing",
      id: "services",
      questions: [
        {
          q: "What services do you offer?",
          a: "We offer several tiers of service: Diagnostic Review (comprehensive medical record analysis), Advocacy Launch Session (1-on-1 consultation), and Deep Dive Consultation (in-depth case analysis with ongoing support). Visit our Services page for detailed descriptions."
        },
        {
          q: "How much do your services cost?",
          a: "Our pricing varies based on the complexity and depth of service needed. We offer transparent pricing on our Pricing page, with options ranging from single consultations to comprehensive membership plans. Contact us for a personalized quote."
        },
        {
          q: "Do you accept insurance?",
          a: "Currently, our services are not covered by insurance as we provide advocacy and consultation services rather than medical treatment. We are a private consulting service and do not bill insurance directly. However, we provide documentation that may be submitted for out-of-network reimbursement. HSA/FSA funds are typically accepted."
        },
        {
          q: "What payment methods do you accept?",
          a: "We accept all major credit cards, debit cards, and HSA/FSA cards through our secure payment processor."
        },
        {
          q: "Is there a cancellation fee?",
          a: "No cancellation fees. You can cancel any service or membership at any time, and you'll retain access through the end of your paid period."
        },
        {
          q: "What is your refund policy?",
          a: "Due to the significant professional time invested from the moment you engage us, we have a limited refund policy. Please review our full Terms of Service and Refund Policy before purchasing. We're happy to discuss any concerns before you commit."
        }
      ]
    },
    {
      title: "Membership Plans",
      id: "membership",
      questions: [
        {
          q: "What's included in a membership?",
          a: "Membership plans include ongoing access to our nurse practitioners, priority scheduling, discounted services, regular check-ins, and personalized health advocacy support. Different tiers offer varying levels of access and benefits."
        },
        {
          q: "What's included in the strategy calls?",
          a: "Strategy calls are focused consultations where we review your progress, address concerns, and plan your next steps. Call frequency and duration vary by tier."
        },
        {
          q: "Can I cancel my membership?",
          a: "Yes, you can cancel your membership at any time. Cancellation takes effect at the end of your current billing period. You'll retain access through the end of your paid period. No cancellation fees. Please refer to our Terms of Service for complete details."
        },
        {
          q: "Can I upgrade or downgrade my membership?",
          a: "Yes! You can change your membership tier at any time. Upgrades take effect immediately (with billing prorated), while downgrades apply at your next billing cycle."
        },
        {
          q: "What are à la carte services?",
          a: "À la carte services are additional offerings available to all clients. Members receive discounts (20-40% depending on tier) on services like follow-up calls, medical script writing, and provider coordination."
        },
        {
          q: "What's the difference between Navigator, Partner, and Advocate tiers?",
          a: "Navigator is our entry-level plan with essential support. Partner includes more frequent consultations and priority access. Advocate is our premium tier with comprehensive support, unlimited messaging, and dedicated case management. Visit our Membership page for full comparisons."
        }
      ]
    },
    {
      title: "Consultations & Process",
      id: "consultations",
      questions: [
        {
          q: "How do I get started?",
          a: "Start by completing our Health Intake Form in the Patient Portal. This helps us understand your situation before your first consultation. Then, schedule a Discovery Call or purchase a service package to begin working with our team."
        },
        {
          q: "What should I prepare for my consultation?",
          a: "Gather your relevant medical records, test results, medication lists, and a list of questions or concerns. The more information you provide, the more comprehensive our analysis can be."
        },
        {
          q: "How long does a typical consultation take?",
          a: "Consultation length varies by service type. Advocacy Launch Sessions are typically 60-90 minutes. Deep Dive Consultations involve multiple sessions over several weeks. We'll discuss timing expectations when you book."
        },
        {
          q: "Are consultations virtual or in-person?",
          a: "Currently, all our consultations are conducted virtually via secure video conferencing. This allows us to serve clients nationwide while maintaining the personal connection essential to effective advocacy."
        }
      ]
    },
    {
      title: "Privacy & Security",
      id: "privacy",
      questions: [
        {
          q: "Is my health information secure?",
          a: "Absolutely. We are fully HIPAA compliant and use encrypted, secure systems to protect your health information. Your privacy is our priority, and we never share your information without your explicit consent."
        },
        {
          q: "Who will see my medical records?",
          a: "Only the CADENCE team members directly involved in your care will access your records. We maintain strict confidentiality protocols and limit access to only those who need it to serve you."
        }
      ]
    }
  ];

  return (
    <div className="legal-page faq-page">
      <SEO
        title="FAQ — Frequently Asked Questions"
        description="Common questions about CADENCE Medical Consulting: what we do, how second opinions work, who we serve, and what makes us different from concierge medicine."
        path="/faq"
      />
      <div className="container">
        <div className="legal-content">
          <h1 className="heading-hero">Frequently Asked Questions</h1>
          <p className="legal-updated">Find answers to common questions about our services</p>

          {/* Sticky section-jump nav (founder spec 2026-02-08
              "add a sticky gold-pill jump nav so visitors can
              teleport to the section they care about"). Foil-gold
              pills mirror the site's plaque language. */}
          <nav className="faq-jump-nav" aria-label="Jump to FAQ section">
            {faqSections.map((section) => (
              <a
                key={section.id}
                href={`#faq-${section.id}`}
                className="faq-jump-pill"
                data-testid={`faq-jump-${section.id}`}
              >
                {section.title}
              </a>
            ))}
          </nav>

          {/* Editorial open-format FAQ — every Q + A is visible at all
              times (founder spec 2026-02-08: "the nested accordion read
              empty and unsymmetrical; reformat as open questions").
              Grouped by section heading, no per-section or per-question
              collapse — long-form, scannable, luxe. */}
          {faqSections.map((section) => (
            <section
              key={section.id}
              id={`faq-${section.id}`}
              className="faq-section-open"
              data-testid={`faq-section-${section.id}`}
            >
              <h2 className="faq-section-heading">{section.title}</h2>
              <div className="faq-questions-open">
                {section.questions.map((item, qIndex) => (
                  <article
                    key={qIndex}
                    className="faq-qa-block"
                    data-testid={`faq-q-${section.id}-${qIndex}`}
                  >
                    <h3 className="faq-q">{item.q}</h3>
                    <p className="faq-a" dangerouslySetInnerHTML={{ __html: item.a }} />
                  </article>
                ))}
              </div>
            </section>
          ))}

          {/* Chatbot Reference Section */}
          <section className="faq-chatbot-section">
            <div className="faq-chatbot-card">
              <div className="faq-chatbot-icon">
                <MessageCircle size={32} />
              </div>
              <div className="faq-chatbot-content">
                <h3>Still have questions?</h3>
                <p>
                  Our AI assistant is available 24/7 to answer your questions. 
                  Click the <strong>&ldquo;Chat with us&rdquo;</strong> button in the bottom right corner 
                  of any page to get instant answers.
                </p>
                <p className="faq-chatbot-note">
                  For complex or specific questions about your case, we recommend scheduling 
                  a <Link to="/contact">Discovery Call</Link> with our team.
                </p>
              </div>
            </div>
          </section>

          {/* Quick Links */}
          <section className="faq-quick-links">
            <h3>Helpful Links</h3>
            <div className="faq-links-grid">
              <Link to="/why-we-exist" className="faq-link-card">
                View Our Services
              </Link>
              <Link to="/pricing" className="faq-link-card">
                See Pricing
              </Link>
              <Link to="/membership" className="faq-link-card">
                Membership Plans
              </Link>
              <Link to="/contact" className="faq-link-card">
                Contact Us
              </Link>
            </div>
          </section>

          {/* Gold Foil C Emblem at Bottom */}
          <div className="faq-bottom-emblem">
            <img 
              src="/cadence-foil-c.png" 
              alt="CADENCE" 
              className="faq-gold-foil-c"
            />
          </div>

        </div>
      </div>
    </div>
  );
};

export default FAQ;
